import { useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { getWorkspaceDetails, deleteWorkspace as apiDeleteWorkspace, getWorkspaces } from '../api'
import { useActiveWorkspace } from '@/state/selection.store'
import posthogAnalytics from '@/lib/posthog'

export function useWorkspaceDetails(options = {}) {
  const [searchParams] = useSearchParams()
  const urlWorkspaceId = searchParams.get('workspace')
  const { workspaceId: storeWorkspaceId, setWorkspace } = useActiveWorkspace()
  const queryClient = useQueryClient()

  const { data: workspacesResponse } = useQuery({
    queryKey: ['workspaces'],
    queryFn: getWorkspaces,
  })

  const availableWorkspaces = workspacesResponse?.data || []
  const fallbackId = availableWorkspaces[0]?.id || availableWorkspaces[0]?._id

  // Priority: options.workspaceId > URL param > store value > fallback > 'default'
  const effectiveWorkspaceId = useMemo(() => {
    return options.workspaceId || urlWorkspaceId || storeWorkspaceId || fallbackId || 'default'
  }, [options.workspaceId, urlWorkspaceId, storeWorkspaceId, fallbackId])

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['workspace', effectiveWorkspaceId],
    queryFn: () => getWorkspaceDetails(effectiveWorkspaceId),
    enabled: Boolean(effectiveWorkspaceId),
    refetchInterval: options.refetchInterval,
    refetchIntervalInBackground: options.refetchIntervalInBackground,
  })

  const workspace = data?.data || null

  useEffect(() => {
    if (!storeWorkspaceId && workspace?.id) {
      setWorkspace(workspace.id)
    } else if (!storeWorkspaceId && workspace?._id) {
      setWorkspace(workspace._id)
    }
  }, [storeWorkspaceId, workspace?.id, workspace?._id, setWorkspace])

  // Update PostHog with current workspace context
  useEffect(() => {
    if (workspace) {
      const activePlans = workspace.activePlans || []
      const hasActivePlan = activePlans.length > 0

      const planTypes = activePlans
        .map((p) => p?.planName || p?.planType || 'unknown')
        .filter(Boolean)
        .join(', ')

      posthogAnalytics.setUserProperties({
        current_workspace_id: workspace.id || workspace._id,
        current_workspace_paid: hasActivePlan,
        current_workspace_plan_count: activePlans.length,
        current_workspace_plan_types: planTypes || 'none',
      })
    }
  }, [workspace])

  const deleteMutation = useMutation({
    mutationFn: (id) => apiDeleteWorkspace(id || workspace?.id || workspace?._id || effectiveWorkspaceId),
    onSuccess: (_, deletedId) => {
      const idToRemove = deletedId || workspace?.id || workspace?._id || effectiveWorkspaceId
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      queryClient.removeQueries({ queryKey: ['workspace', idToRemove] })

      if (!options.workspaceId && (storeWorkspaceId === idToRemove || storeWorkspaceId === effectiveWorkspaceId)) {
        setWorkspace(null)
      }
    },
  })

  return {
    workspace,
    isLoading,
    isFetching,
    error,
    refetch,
    deleteWorkspace: deleteMutation.mutateAsync,
    deleting: deleteMutation.isPending,
  }
}
